import React, { useEffect, useRef, useState } from "react";
import {
  createChart,
  ColorType,
  LineSeries,
  CandlestickSeries,
  AreaSeries,
  HistogramSeries,
} from "lightweight-charts";
import { useTheme } from "../context/ThemeContext";

// Light + dark token sets for the lightweight-charts canvas. Kept in sync
// with the rest of the app via the ThemeContext.
const CHART_THEMES = {
  light: {
    background: "#FFFFFF",
    textColor: "#6B7068",
    grid: "#F0EDE8",
    border: "#EAE6E1",
    crosshair: "#547A8F",
  },
  dark: {
    background: "#161B22",
    textColor: "#B8C0CC",
    grid: "#1F2630",
    border: "#2A3340",
    crosshair: "#3FB962",
  },
};

/**
 * Trading chart with Line / Area / Candle toggles built on lightweight-charts.
 * points: [{ t, o, h, l, c, v }]
 */
export default function TradingChart({ points = [], mode = "area", trendUp = true, height = 420 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const { resolved } = useTheme();
  const tokens = CHART_THEMES[resolved] || CHART_THEMES.light;

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: tokens.background },
        textColor: tokens.textColor,
        fontFamily: "IBM Plex Mono, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: tokens.grid },
        horzLines: { color: tokens.grid },
      },
      rightPriceScale: { borderColor: tokens.border },
      timeScale: { borderColor: tokens.border, timeVisible: true, secondsVisible: false },
      crosshair: {
        mode: 1,
        horzLine: { color: tokens.crosshair, style: 2, width: 1 },
        vertLine: { color: tokens.crosshair, style: 2, width: 1 },
      },
      height,
      autoSize: true,
      localization: { locale: 'en-US' },
    });
    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [height]);

  // Hot-swap colors when the theme flips — avoids the chart staying on a
  // white canvas after the user toggles to dark mode.
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: tokens.background },
        textColor: tokens.textColor,
      },
      grid: {
        vertLines: { color: tokens.grid },
        horzLines: { color: tokens.grid },
      },
      rightPriceScale: { borderColor: tokens.border },
      timeScale: { borderColor: tokens.border },
      crosshair: {
        horzLine: { color: tokens.crosshair },
        vertLine: { color: tokens.crosshair },
      },
    });
  }, [tokens]);

  useEffect(() => {
    if (!chartRef.current || !points || points.length === 0) return;
    const chart = chartRef.current;

    // Remove previous series
    if (seriesRef.current) {
      try { chart.removeSeries(seriesRef.current); } catch (e) { console.warn("TradingChart: series cleanup failed", e); }
      seriesRef.current = null;
    }

    const trendColor = trendUp ? "#5C8D73" : "#C66B59";
    // lightweight-charts requires UNIX timestamp (seconds) or yyyy-mm-dd
    const toTs = (iso) => Math.floor(new Date(iso).getTime() / 1000);

    // Deduplicate & sort ascending (lightweight-charts requires strictly increasing)
    const seen = new Map();
    for (const p of points) {
      if (p.c == null) continue;
      const ts = toTs(p.t);
      seen.set(ts, p);
    }
    const sorted = [...seen.entries()].sort((a, b) => a[0] - b[0]);

    if (mode === "candle") {
      const s = chart.addSeries(CandlestickSeries, {
        upColor: "#5C8D73",
        downColor: "#C66B59",
        borderVisible: false,
        wickUpColor: "#5C8D73",
        wickDownColor: "#C66B59",
      });
      const data = sorted.map(([ts, p]) => ({
        time: ts,
        open: p.o ?? p.c,
        high: p.h ?? p.c,
        low: p.l ?? p.c,
        close: p.c,
      }));
      s.setData(data);
      seriesRef.current = s;
    } else if (mode === "line") {
      const s = chart.addSeries(LineSeries, {
        color: trendColor,
        lineWidth: 2,
      });
      s.setData(sorted.map(([ts, p]) => ({ time: ts, value: p.c })));
      seriesRef.current = s;
    } else {
      // area
      const s = chart.addSeries(AreaSeries, {
        lineColor: trendColor,
        topColor: `${trendColor}40`,
        bottomColor: `${trendColor}00`,
        lineWidth: 2,
      });
      s.setData(sorted.map(([ts, p]) => ({ time: ts, value: p.c })));
      seriesRef.current = s;
    }

    chart.timeScale().fitContent();
  }, [points, mode, trendUp]);

  return (
    <div
      ref={containerRef}
      data-testid="trading-chart"
      style={{ width: "100%", height }}
    />
  );
}
